import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, CheckCircle, XCircle, Fire, X, ArrowsSplit, Plus } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";

import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { fetchAISection } from "@/lib/api";
const MODEL_FAMILIES = ["All", "GPT", "Claude", "Gemini", "Llama", "Mistral", "DeepSeek", "Qwen"];
import { stripHtml } from "@/lib/stripHtml";

const SORT_OPTIONS = [
  { value: "rank",    label: "Rank" },
  { value: "mmlu",   label: "MMLU Score" },
  { value: "code",   label: "Coding Score" },
  { value: "newest", label: "Newest" },
];

// ── Helpers ────────────────────────────────────────────────────────────────────
function isNewToday(item) {
  if (!item?.created_at) return false;
  return Date.now() - new Date(item.created_at).getTime() < 24 * 60 * 60 * 1000;
}

// ── Skeleton ───────────────────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 animate-pulse">
      <div className="h-4 bg-slate-200 rounded w-1/2 mb-3" />
      <div className="h-3 bg-slate-200 rounded w-3/4 mb-4" />
      <div className="flex gap-2 mb-4">
        {[1,2,3].map(i => <div key={i} className="h-5 bg-slate-200 rounded-full w-14" />)}
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[1,2,3,4].map(i => <div key={i} className="h-10 bg-slate-200 rounded-lg" />)}
      </div>
    </div>
  );
}

// ── Model card ─────────────────────────────────────────────────────────────────
function ModelCard({ model, isSelected, onToggleCompare }) {
  const slug = model.slug || model.id;
  const capColor = (c) => {
    const map = {
      "Text": "bg-blue-50 text-blue-700",
      "Code": "bg-emerald-50 text-emerald-700",
      "Vision": "bg-purple-50 text-purple-700",
      "Audio": "bg-amber-50 text-amber-700",
      "Video": "bg-rose-50 text-rose-700",
      "Reasoning": "bg-indigo-50 text-indigo-700",
      "Agents": "bg-cyan-50 text-cyan-700",
      "Math": "bg-orange-50 text-orange-700",
      "Function Calling": "bg-teal-50 text-teal-700",
    };
    return map[c] || "bg-slate-100 text-slate-600";
  };

  const benchmarks = model.benchmarks || {};
  const pricing = model.pricing || {};
  const today = isNewToday(model);

  return (
    <Link to={`/trending-ai/models/${slug}`} className={`relative block bg-white border rounded-2xl p-5 hover:shadow-md transition-all group ${isSelected ? "border-blue-500 shadow-md shadow-blue-100" : "border-slate-200 hover:border-blue-300"}`}>
      {/* Compare toggle */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleCompare(model); }}
        title={isSelected ? "Remove from comparison" : "Add to comparison"}
        className={`absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
          isSelected
            ? "bg-blue-600 text-white"
            : "bg-slate-100 text-slate-400 hover:bg-blue-100 hover:text-blue-600"
        }`}
      >
        {isSelected ? <CheckCircle size={14} weight="fill" /> : <Plus size={12} weight="bold" />}
      </button>

      <div className="flex items-start justify-between mb-3 pr-8">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {model.rank && <span className="text-xs font-bold text-slate-400">#{model.rank}</span>}
            <h3 className="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{stripHtml(model.name)}</h3>
            {today && <span className="text-[10px] font-bold bg-sky-500 text-white px-1.5 py-0.5 rounded">TODAY</span>}
            {(model.isNew || model.is_new) && !today && (
              <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded">NEW</span>
            )}
            {model.hot && (
              <span className="text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5">
                <Fire size={7} weight="fill" /> HOT
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{stripHtml(model.provider)} · Released {model.releasedAt || model.release_date}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="flex items-center gap-1 text-xs text-slate-500">
            {model.openSource ? (
              <><CheckCircle size={12} className="text-emerald-500" weight="fill" /> Open Source</>
            ) : (
              <><XCircle size={12} className="text-slate-400" weight="fill" /> Closed</>
            )}
          </span>
          <span className="flex items-center gap-1 text-xs text-slate-500">
            {model.apiAvailable !== false ? (
              <><CheckCircle size={12} className="text-blue-500" weight="fill" /> API</>
            ) : (
              <><XCircle size={12} className="text-slate-400" weight="fill" /> No API</>
            )}
          </span>
        </div>
      </div>

      <p className="text-xs text-slate-600 leading-relaxed mb-4">{stripHtml(model.description)}</p>

      {model.capabilities && model.capabilities.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {model.capabilities.map((c) => (
            <span key={c} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${capColor(c)}`}>{c}</span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {[
          { label: "MMLU",      value: benchmarks.MMLU },
          { label: "HumanEval", value: benchmarks.HumanEval },
          { label: "MATH",      value: benchmarks.MATH },
          { label: "GPQA",      value: benchmarks.GPQA },
        ].map(({ label, value }) => (
          <div key={label} className="bg-slate-50 rounded-lg p-2 text-center">
            <div className="text-sm font-bold text-slate-900">{value ?? "—"}</div>
            <div className="text-[10px] text-slate-400 font-medium">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500 border-t border-slate-100 pt-3">
        <span>{model.contextWindow || model.context_window || "—"} context</span>
        <span>{model.parameters || "—"}</span>
        <span className="font-medium text-slate-700">{pricing.input || "—"} in</span>
      </div>
    </Link>
  );
}

// ── Compare bar ────────────────────────────────────────────────────────────────
function CompareBar({ models, onRemove, onClear, onOpen }) {
  if (models.length === 0) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-2xl px-4 py-3 flex items-center gap-3">
      <ArrowsSplit size={16} className="text-blue-600 shrink-0" />
      <div className="flex-1 flex items-center gap-2 overflow-x-auto">
        {models.map((m) => (
          <span key={m.id || m.slug} className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-800 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap shrink-0">
            {m.name}
            <button onClick={() => onRemove(m)} className="text-blue-400 hover:text-blue-700 transition-colors">
              <X size={11} weight="bold" />
            </button>
          </span>
        ))}
        {models.length < 3 && (
          <span className="text-xs text-slate-400 whitespace-nowrap shrink-0">
            + {3 - models.length} more to compare
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={onClear} className="text-xs text-slate-500 hover:text-slate-800 transition-colors">
          Clear
        </button>
        <button
          onClick={onOpen}
          disabled={models.length < 2}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-lg transition-colors"
        >
          <ArrowsSplit size={12} /> Compare {models.length}
        </button>
      </div>
    </div>
  );
}

// ── Compare modal ──────────────────────────────────────────────────────────────
const PROVIDER_COLORS = {
  OpenAI: "bg-emerald-100 text-emerald-800",
  Anthropic: "bg-purple-100 text-purple-800",
  "Google DeepMind": "bg-blue-100 text-blue-800",
  "Meta AI": "bg-indigo-100 text-indigo-800",
  DeepSeek: "bg-amber-100 text-amber-800",
  "Mistral AI": "bg-rose-100 text-rose-800",
};

function CompareRow({ label, values, highlight = false }) {
  return (
    <tr className={highlight ? "bg-slate-50" : ""}>
      <td className="py-3 px-4 text-xs font-semibold text-slate-500 border-r border-slate-100 w-32 shrink-0">{label}</td>
      {values.map((v, i) => (
        <td key={i} className="py-3 px-4 text-xs text-slate-800 border-r border-slate-100 last:border-r-0">{v}</td>
      ))}
    </tr>
  );
}

function CompareModal({ models, onClose }) {
  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const best = (key, fn = Math.max) => {
    const vals = models.map((m) => Number(m.benchmarks?.[key]) || 0);
    const top = fn(...vals);
    return vals.map((v) => v === top && v > 0);
  };

  const bestMMLU      = best("MMLU");
  const bestHumanEval = best("HumanEval");
  const bestMATH      = best("MATH");
  const bestGPQA      = best("GPQA");

  function ScoreCell({ value, isBest }) {
    if (!value) return <span className="text-slate-400">—</span>;
    return (
      <span className={`font-bold ${isBest ? "text-blue-600" : "text-slate-800"}`}>
        {value}
        {isBest && <span className="ml-1 text-[9px] font-bold text-blue-500">▲ BEST</span>}
      </span>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <ArrowsSplit size={18} className="text-blue-600" />
            <h2 className="text-base font-bold text-slate-900">Model Comparison</h2>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700 transition-colors rounded-lg hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        {/* Model header row */}
        <div className="px-6 pt-5 pb-3 grid border-b border-slate-200" style={{ gridTemplateColumns: `8rem repeat(${models.length}, 1fr)` }}>
          <div />
          {models.map((m) => (
            <div key={m.id || m.slug} className="px-2">
              <div className="font-bold text-sm text-slate-900">{m.name}</div>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full inline-block mt-1 ${PROVIDER_COLORS[m.provider] || "bg-slate-100 text-slate-600"}`}>
                {m.provider}
              </span>
            </div>
          ))}
        </div>

        {/* Comparison table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <tbody>
              {/* General */}
              <tr className="bg-slate-50 border-b border-slate-200">
                <td colSpan={models.length + 1} className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">General</td>
              </tr>
              <CompareRow label="Open Source" values={models.map((m) => m.openSource ? <span className="text-emerald-600 font-semibold">✓ Yes</span> : <span className="text-slate-400">No</span>)} />
              <CompareRow label="API Access"  values={models.map((m) => m.apiAvailable !== false ? <span className="text-blue-600 font-semibold">✓ Yes</span> : <span className="text-slate-400">No</span>)} highlight />
              <CompareRow label="Context"     values={models.map((m) => m.contextWindow || m.context_window || "—")} />
              <CompareRow label="Parameters"  values={models.map((m) => m.parameters || "—")} highlight />
              <CompareRow label="Released"    values={models.map((m) => m.releasedAt || m.release_date || "—")} />

              {/* Pricing */}
              <tr className="bg-slate-50 border-b border-slate-200 border-t">
                <td colSpan={models.length + 1} className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Pricing</td>
              </tr>
              <CompareRow label="Input / 1M"  values={models.map((m) => m.pricing?.input  || "—")} />
              <CompareRow label="Output / 1M" values={models.map((m) => m.pricing?.output || "—")} highlight />

              {/* Benchmarks */}
              <tr className="bg-slate-50 border-b border-slate-200 border-t">
                <td colSpan={models.length + 1} className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Benchmarks</td>
              </tr>
              <CompareRow label="MMLU"
                values={models.map((m, i) => <ScoreCell key={i} value={m.benchmarks?.MMLU} isBest={bestMMLU[i]} />)} />
              <CompareRow label="HumanEval"
                values={models.map((m, i) => <ScoreCell key={i} value={m.benchmarks?.HumanEval} isBest={bestHumanEval[i]} />)} highlight />
              <CompareRow label="MATH"
                values={models.map((m, i) => <ScoreCell key={i} value={m.benchmarks?.MATH} isBest={bestMATH[i]} />)} />
              <CompareRow label="GPQA"
                values={models.map((m, i) => <ScoreCell key={i} value={m.benchmarks?.GPQA} isBest={bestGPQA[i]} />)} highlight />

              {/* Capabilities */}
              <tr className="bg-slate-50 border-b border-slate-200 border-t">
                <td colSpan={models.length + 1} className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Capabilities</td>
              </tr>
              <CompareRow label="Capabilities"
                values={models.map((m) => (
                  <div className="flex flex-wrap gap-1">
                    {(m.capabilities || []).map((c) => (
                      <span key={c} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">{c}</span>
                    ))}
                    {(!m.capabilities || m.capabilities.length === 0) && <span className="text-slate-400">—</span>}
                  </div>
                ))} />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function AIModelsPage() {
  const [search, setSearch]     = useState("");
  const [family, setFamily]     = useState("All");
  const [sort, setSort]         = useState("rank");
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [compareList, setCompareList] = useState([]);  // max 3 models
  const [showModal, setShowModal]     = useState(false);

  const fetchFn = useCallback(
    (page) => fetchAISection("models", { page, page_size: 12 }),
    []
  );
  const { items: apiItems, loading, loadingMore, hasMore, error, loadMore } = useInfiniteFeed({
    fetchFn,
    feedKey: "ai-models",
    getCacheKey: (page) => `ai-models-p${page}`,
    ttlMs: 10 * 60 * 1000,
  });

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const allItems = useMemo(
    () => apiItems,
    [apiItems]
  );

  const families = useMemo(() => {
    const fromApi = [...new Set(allItems.map((m) => m.family).filter(Boolean))];
    return fromApi.length > 0 ? ["All", ...fromApi] : MODEL_FAMILIES;
  }, [allItems]);

  const filtered = useMemo(() => allItems
    .filter((m) => {
      const matchFamily = family === "All" || m.family === family;
      const matchSearch = !search || m.name.toLowerCase().includes(search.toLowerCase()) || (m.provider || "").toLowerCase().includes(search.toLowerCase());
      const matchOpen = !onlyOpen || m.openSource;
      return matchFamily && matchSearch && matchOpen;
    })
    .sort((a, b) => {
      if (sort === "mmlu")   return (b.benchmarks?.MMLU || 0) - (a.benchmarks?.MMLU || 0);
      if (sort === "code")   return (b.benchmarks?.HumanEval || 0) - (a.benchmarks?.HumanEval || 0);
      if (sort === "newest") return new Date(b.releasedAt || b.release_date || 0) - new Date(a.releasedAt || a.release_date || 0);
      return (a.rank || 99) - (b.rank || 99);
    }),
    [allItems, family, search, onlyOpen, sort]
  );

  const compareIds = useMemo(() => new Set(compareList.map((m) => m.id || m.slug)), [compareList]);

  const handleToggleCompare = useCallback((model) => {
    const key = model.id || model.slug;
    setCompareList((prev) => {
      if (prev.some((m) => (m.id || m.slug) === key)) {
        return prev.filter((m) => (m.id || m.slug) !== key);
      }
      if (prev.length >= 3) return prev; // max 3
      return [...prev, model];
    });
  }, []);

  return (
    <>
      <SEO
        title="AI Models — Complete Model Database · TrendMing"
        description="Compare GPT, Claude, Gemini, Llama, Mistral, DeepSeek, and Qwen models. Full benchmark scores, pricing, context windows, and capabilities."
        canonical="https://trendming.com/trending-ai/models"
      />
      <AINavBar />
      <div className={`max-w-[1400px] mx-auto px-4 md:px-6 py-6 ${compareList.length > 0 ? "pb-20" : ""}`}>
        <div className="mb-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">AI Intelligence · Models</span>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">AI Model Directory</h1>
          <p className="text-slate-500 text-sm mt-1">
            Compare {allItems.length}+ AI models with benchmarks, pricing, and capabilities.
            <span className="ml-2 text-blue-600 font-medium">Select up to 3 to compare side-by-side.</span>
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[180px]">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search models…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              />
            </div>
            <select value={family} onChange={(e) => setFamily(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white">
              {families.map((f) => <option key={f}>{f}</option>)}
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white">
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
              <span className="text-sm text-slate-600 whitespace-nowrap">Open Source only</span>
            </label>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-5 text-xs text-slate-500">
          <span>{filtered.length} models</span>
          <span>·</span>
          <span>{allItems.filter((m) => m.isNew || m.is_new).length} new this month</span>
          <span>·</span>
          <span>{allItems.filter((m) => m.openSource).length} open source</span>
          {allItems.some(isNewToday) && (
            <>
              <span>·</span>
              <span className="text-sky-600 font-semibold">{allItems.filter(isNewToday).length} added today</span>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          {!loading && filtered.map((model) => (
            <ModelCard
              key={model.id || model.slug}
              model={model}
              isSelected={compareIds.has(model.id || model.slug)}
              onToggleCompare={handleToggleCompare}
            />
          ))}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-400">
              <p className="text-lg font-medium">No models found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          )}
        </div>

        {hasMore && !loading && <div ref={sentinelRef} className="h-4" />}
        {loadingMore && <div className="text-center py-4 text-xs text-slate-400">Loading more…</div>}
        {error && !loading && (
          <div className="text-center py-8 text-slate-500">
            <p className="text-red-500 font-medium">Could not load models</p>
            <p className="text-xs mt-1">Content is being refreshed — check back shortly.</p>
          </div>
        )}

        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center">
          <h3 className="text-base font-bold text-slate-900 mb-2">Want detailed model comparisons?</h3>
          <p className="text-sm text-slate-500 mb-4">Visit our Benchmark Center for side-by-side comparisons across all key metrics.</p>
          <Link to="/trending-ai/benchmarks" className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm rounded-xl transition-colors">
            View Benchmark Center
          </Link>
        </div>
      </div>

      {/* Floating compare bar */}
      <CompareBar
        models={compareList}
        onRemove={(m) => handleToggleCompare(m)}
        onClear={() => setCompareList([])}
        onOpen={() => setShowModal(true)}
      />

      {/* Compare modal */}
      {showModal && (
        <CompareModal models={compareList} onClose={() => setShowModal(false)} />
      )}
    </>
  );
}
